<script setup>
/**
 * The lesson's picture, enlarged (lesson-redo DESIGN.md F, rev 9).
 *
 * Wide: the picture at full size (object-fit: contain, never past the
 * viewport) on a dark scrim. Phone: centred on black. The caption sits over
 * the picture's foot at every width, in its two beats, on a dark gradient
 * (picture-caption DESIGN.md rev 1, 2026-10-30; the reading's enlarged view
 * matches it). It rises in just after the picture.
 *
 * × (focused on open), Esc, or a tap on the scrim closes it; Esc closes only
 * the picture, never the lesson sheet under it. The opener returns focus.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue'
import CaptionBeats from './CaptionBeats.vue'

const props = defineProps({
  src: { type: String, required: true },
  caption: { type: String, default: '' },
})
const emit = defineEmits(['close'])
const closeBtn = ref(null)

function onKey(e) {
  if (e.key !== 'Escape') return
  // Captured first, so the sheet's own Esc never hears it.
  e.stopImmediatePropagation()
  e.preventDefault()
  emit('close')
}
onMounted(() => {
  window.addEventListener('keydown', onKey, true)
  const b = closeBtn.value
  if (b) { try { b.focus({ preventScroll: true }) } catch (e) { b.focus() } }
})
onBeforeUnmount(() => window.removeEventListener('keydown', onKey, true))
</script>

<template>
  <Teleport to="body">
    <div class="zm" role="dialog" aria-modal="true" :aria-label="props.caption || 'The picture'" data-testid="picture-view"
         @click.self="emit('close')">
      <button ref="closeBtn" type="button" class="zm-x" aria-label="Close the picture" data-testid="picture-view-close" @click="emit('close')">
        <span aria-hidden="true">×</span>
      </button>
      <figure class="zm-f" @click.self="emit('close')">
        <img :src="props.src" :alt="props.caption" />
        <figcaption v-if="props.caption"><CaptionBeats :text="props.caption" /></figcaption>
      </figure>
    </div>
  </Teleport>
</template>

<style scoped>
.zm {
  position: fixed; inset: 0; z-index: 9500; display: flex; align-items: center; justify-content: center; padding: 40px 72px;
  background: rgba(6, 2, 14, .92); animation: zm-fade .2s ease-out; box-sizing: border-box;
}
.zm-f { position: relative; display: inline-block; line-height: 0; margin: 0; max-width: min(1200px, 100%); max-height: 100%; }
.zm-f img { display: block; max-width: 100%; max-height: calc(100dvh - 80px); object-fit: contain; border-radius: 10px; box-shadow: 0 30px 80px -20px #000; }
.zm-f figcaption { position: absolute; left: 0; right: 0; bottom: 0; margin: 0; box-sizing: border-box; padding: 56px 28px 20px; border-radius: 0 0 10px 10px; text-align: center; text-wrap: balance; font: italic 500 clamp(17px, 1.7vw, 22px)/1.35 'Letter Serif', 'Cormorant Garamond', Georgia, serif; color: #fbf3e0; text-shadow: 0 1px 10px rgba(0, 0, 0, .55); background: linear-gradient(to top, rgba(8, 4, 20, .92), rgba(8, 4, 20, .55) 55%, rgba(8, 4, 20, 0)); animation: zm-cap-in 520ms cubic-bezier(.2, .7, .2, 1) 200ms both; }
.zm-x { position: fixed; top: 16px; right: 16px; z-index: 2; width: 48px; height: 48px; border: 0; background: none; cursor: pointer; display: grid; place-items: center; padding: 0; }
.zm-x span { width: 40px; height: 40px; border-radius: 50%; display: grid; place-items: center; background: #2a1845; color: #e8d8ff;
  border: 1px solid rgba(231, 201, 131, .35); font: 500 24px/1 Inter, system-ui, sans-serif; }
.zm-x:hover span { color: #ffdfa3; }
.zm-x:focus-visible { outline: 2px solid #e7c983; outline-offset: -2px; border-radius: 50%; }
@media (max-width: 767px) {
  /* The picture centred on black; the caption on its foot, not at the screen's bottom. */
  .zm { padding: 0; align-items: center; background: #000; }
  .zm-f { max-width: none; width: 100%; display: block; }
  .zm-f img { width: 100%; max-height: 100dvh; border-radius: 0; box-shadow: none; }
  .zm-f figcaption { padding: 40px 20px 18px; border-radius: 0; background: linear-gradient(transparent, rgba(0, 0, 0, .85)); font-size: 18px; }
}
@keyframes zm-fade { from { opacity: 0; } }
@keyframes zm-cap-in { from { opacity: 0; transform: translateY(6px); } }
@media (prefers-reduced-motion: reduce) { .zm, .zm-f figcaption { animation: none; } }
</style>
